import { TableQueryType } from '../store/schema/TableQuery';
import TableView from '../store/schema/TableView';
import TableViewConversationRelation from '../store/schema/TableViewConversationRelation';
import TableViewItemRelation from '../store/schema/TableViewItemRelation';
import getTableConversationRelation from '../utils/getTableConversationRelation';
import getTableItemRelation from '../utils/getTableItemRelation';
import isItemOfMessageType from '../utils/isItemOfMessageType';
import * as pinningUtils from '../utils/pinningUtils';
import { returnTopExecutingActionDatapoint } from 'owa-analytics';
import { ClientItemId } from 'owa-client-ids';
import selectConversationById from 'owa-mail-list-store/lib/selectors/selectConversationById';
import { InstrumentationContext, mailStore } from 'owa-mail-store';
import BaseItem from 'owa-mail-store/lib/store/schema/BaseItem';
import EmailAddressWrapper from 'owa-service/lib/contract/EmailAddressWrapper';
import ExecuteSearchSortOrder from 'owa-service/lib/contract/ExecuteSearchSortOrder';
import FlagStatus from 'owa-service/lib/contract/FlagStatus';
import Message from 'owa-service/lib/contract/Message';
import ReactListViewType from 'owa-service/lib/contract/ReactListViewType';
import RetentionTagType from 'owa-service/lib/contract/RetentionTagType';
import { trace } from 'owa-trace';

import {
    getIsTableConversationRelationSnoozed,
    getIsTableItemDataSnoozed,
} from '../utils/snoozeUtils';

export { TableViewConversationRelation, TableViewItemRelation };

enum MailRowDataPropertyType {
    ArchiveTag,
    CanDelete,
    Categories,
    ConversationId,
    EffectiveMentioned,
    ExecuteSearchSortOrder,
    FlagStatus,
    Importance,
    InstrumentationContext,
    IsDraft,
    IsPinned,
    IsSnoozed,
    IsSubmitted,
    ItemIds,
    LastDeliveryOrRenewTimeStamp,
    LastDeliveryTimeStamp,
    LastModifiedTimeStamp,
    LastSenderMailbox,
    LastSenderSMTP,
    ParentFolderId,
    PolicyTag,
    RowClientItemId,
    RowIdString,
    Size,
    Subject,
    UniqueSenders,
    UnreadCount,
    ValidCouponIndexes,
}

/**
 * Get canDelete for a row
 * @param rowKey rowKey of row
 * @param tableView the table that contains the row data
 * @return boolean for canDelete
 */
export function getCanDelete(rowKey: string, tableView: TableView): boolean {
    return getRowDataProperty(rowKey, tableView, MailRowDataPropertyType.CanDelete) as boolean;
}

/**
 * Get categories for a row
 * @param rowKey rowKey of row
 * @param tableView the table that contains the row data
 * @return a collection of categories
 */
export function getCategories(rowKey: string, tableView: TableView): string[] {
    return getRowDataProperty(rowKey, tableView, MailRowDataPropertyType.Categories) as string[];
}

/**
 * Get conversation id for a row
 * @param rowKey rowKey of row
 * @param tableView the table that contains the row data
 * @return a collection of categories
 */
export function getConversationId(rowKey: string, tableView: TableView): string {
    return getRowDataProperty(rowKey, tableView, MailRowDataPropertyType.ConversationId) as string;
}

/**
 * Get if a row is pinned
 * @param rowKey rowKey of row
 * @param tableView the table that contains the row data
 * @return true if a row is pinned, false otherwise
 */
export function getIsPinned(rowKey: string, tableView: TableView): boolean {
    return getRowDataProperty(rowKey, tableView, MailRowDataPropertyType.IsPinned) as boolean;
}

/**
 * Get the itemIds
 * @param rowKey rowKey of row
 * @param tableView the table that contains the row data
 * @return itemIds if row is Conversation, or itemId if row is Item
 */
export function getItemIds(rowKey: string, tableView: TableView): string[] {
    return getRowDataProperty(rowKey, tableView, MailRowDataPropertyType.ItemIds) as string[];
}

/**
 * Get the executeSearchSortOrder of a row
 * @param rowKey rowKey of row
 * @param tableView the table that contains the row data
 * @return the executeSearchSortOrder
 */
export function getExecuteSearchSortOrder(
    rowKey: string,
    tableView: TableView
): ExecuteSearchSortOrder {
    return getRowDataProperty(
        rowKey,
        tableView,
        MailRowDataPropertyType.ExecuteSearchSortOrder
    ) as ExecuteSearchSortOrder;
}

/**
 * Get the getUniqueSenders of a row
 * @param rowKey rowKey of row
 * @param tableView the table that contains the row data
 * @return the getUniqueSenders
 */
export function getUniqueSenders(rowKey: string, tableView: TableView): string[] {
    return getRowDataProperty(rowKey, tableView, MailRowDataPropertyType.UniqueSenders) as string[];
}

/**
 * Get the flag status of a row
 * @param rowKey rowKey of row
 * @param tableView the table that contains the row data
 * @return the flag status
 */
export function getFlagStatus(rowKey: string, tableView: TableView): FlagStatus {
    return getRowDataProperty(rowKey, tableView, MailRowDataPropertyType.FlagStatus) as FlagStatus;
}

/**
 * Get the effective mentioned of a row
 * @param rowKey rowKey of row
 * @param tableView the table that contains the row data
 * @return whether there is a mention
 */
export function getEffectiveMentioned(rowKey: string, tableView: TableView): boolean {
    return getRowDataProperty(
        rowKey,
        tableView,
        MailRowDataPropertyType.EffectiveMentioned
    ) as boolean;
}

/**
 * Get the lastDeliveryOrRenewTimeStamp of a row
 * @param rowKey rowKey of row
 * @param tableView the table that contains the row data
 * @return the lastDeliveryOrRenewTimeStamp
 */
export function getLastDeliveryOrRenewTimeStamp(rowKey: string, tableView: TableView): string {
    return getRowDataProperty(
        rowKey,
        tableView,
        MailRowDataPropertyType.LastDeliveryOrRenewTimeStamp
    ) as string;
}

/**
 * Get the lastModifiedTimeStamp of a row
 * @param rowKey rowKey of row
 * @param tableView the table that contains the row data
 * @return the lastDeliveryOrRenewTimeStamp
 */
export function getLastModifiedTimeStamp(rowKey: string, tableView: TableView): string {
    return getRowDataProperty(
        rowKey,
        tableView,
        MailRowDataPropertyType.LastModifiedTimeStamp
    ) as string;
}

/**
 * Get the lastDeliveryTimeStamp of a row
 * @param rowKey rowKey of row
 * @param tableView the table that contains the row data
 * @return the lastDeliveryTimeStamp
 */
export function getLastDeliveryTimeStamp(rowKey: string, tableView: TableView): string {
    return getRowDataProperty(
        rowKey,
        tableView,
        MailRowDataPropertyType.LastDeliveryTimeStamp
    ) as string;
}

/**
 * Get the subject of a row
 * @param rowKey rowKey of row
 * @param tableView the table that contains the row data
 * @return the subject
 */
export function getSubject(rowKey: string, tableView: TableView): string {
    return getRowDataProperty(rowKey, tableView, MailRowDataPropertyType.Subject) as string;
}

/**
 * Get the last sender mailbox of a row
 * @param rowKey key of the row data
 * @param tableView the table that contains the row data
 * @return the last sender mailbox of a row
 */
export function getLastSenderMailbox(rowKey: string, tableView: TableView): EmailAddressWrapper {
    return getRowDataProperty(
        rowKey,
        tableView,
        MailRowDataPropertyType.LastSenderMailbox
    ) as EmailAddressWrapper;
}

/**
 * Get the last sender smtp address of a row
 * @param rowKey rowKey of row
 * @param tableView the table that contains the row data
 * @return the last sender smtp address of a row
 */
export function getLastSenderSMTP(rowKey: string, tableView: TableView): string {
    return getRowDataProperty(rowKey, tableView, MailRowDataPropertyType.LastSenderSMTP) as string;
}

/**
 * Get the unread count of a row
 * @param rowKey rowKey of row
 * @param tableView the table that contains the row data
 * @return the unread count
 */
export function getUnreadCount(rowKey: string, tableView: TableView): number {
    return getRowDataProperty(rowKey, tableView, MailRowDataPropertyType.UnreadCount) as number;
}

/**
 * Get the size of a row
 * @param rowKey rowKey of row
 * @param tableView the table that contains the row data
 * @return the unread count
 */
export function getSize(rowKey: string, tableView: TableView): number {
    return getRowDataProperty(rowKey, tableView, MailRowDataPropertyType.Size) as number;
}

/**
 * Get the string type rowId of the row
 * @param rowKey rowKey of row
 * @param tableView the table that contains the row data
 * @return the row id string
 */
export function getRowIdString(rowKey: string, tableView: TableView): string {
    return getRowDataProperty(rowKey, tableView, MailRowDataPropertyType.RowIdString) as string;
}

/**
 * Get the ClientItemId type rowId of the row
 * @param rowKey rowKey of row
 * @param tableView the table that contains the row data
 * @return the row client id
 */
export function getRowClientItemId(rowKey: string, tableView: TableView): ClientItemId {
    return getRowDataProperty(
        rowKey,
        tableView,
        MailRowDataPropertyType.RowClientItemId
    ) as ClientItemId;
}

/**
 * Get the source folder id of the row data
 * @param rowKey rowKey of row
 * @param tableView the table that contains the row data
 * @return the source folder id
 */
export function getParentFolderId(rowKey: string, tableView: TableView): string {
    return getRowDataProperty(rowKey, tableView, MailRowDataPropertyType.ParentFolderId) as string;
}

/**
 * Get the size of a row
 * @param rowKey rowKey of row
 * @param tableView the table that contains the row data
 * @return the 3S search result instrumentation context
 */
export function getInstrumentationContext(
    rowKey: string,
    tableView: TableView
): InstrumentationContext {
    return getRowDataProperty(
        rowKey,
        tableView,
        MailRowDataPropertyType.InstrumentationContext
    ) as InstrumentationContext;
}

/**
 * Get if an item is a draft
 * @param rowKey rowKey of row
 * @param tableView the table that contains the row data
 * @return true if an item is a draft, false otherwise
 */
export let getIsDraft = function getIsDraft(rowKey: string, tableView: TableView): boolean {
    return getRowDataProperty(rowKey, tableView, MailRowDataPropertyType.IsDraft) as boolean;
};

/**
 * Get if an item is submitted
 * @param rowKey rowKey of row
 * @param tableView the table that contains the row data
 * @return true if an item is submitted, false otherwise
 */
export let getIsSubmitted = function getIsSubmitted(rowKey: string, tableView: TableView): boolean {
    return getRowDataProperty(rowKey, tableView, MailRowDataPropertyType.IsSubmitted) as boolean;
};

/**
 * Get if an item is snoozed
 * @param rowKey rowKey of row
 * @param tableView the table that contains the row data
 * @return true if an item is snoozed, false otherwise
 */
export function getIsSnoozed(rowKey: string, tableView: TableView): boolean {
    return getRowDataProperty(rowKey, tableView, MailRowDataPropertyType.IsSnoozed) as boolean;
}

/**
 * Get valid coupon indexes for a row
 * @param rowKey rowKey of row
 * @param tableView the table that contains the row data
 * @return indexes of valid coupons
 */
export function getValidCouponIndexes(rowKey: string, tableView: TableView): number[] {
    return getRowDataProperty(
        rowKey,
        tableView,
        MailRowDataPropertyType.ValidCouponIndexes
    ) as number[];
}

/**
 * Get importance for a row
 * @param rowKey rowKey of row
 * @param tableView the table that contains the row data
 * @return importance of row
 */
export function getImportance(rowKey: string, tableView: TableView): string {
    return getRowDataProperty(rowKey, tableView, MailRowDataPropertyType.Importance) as string;
}

/**
 * Get archive tag for a row
 * @param rowKey rowKey of row
 * @param tableView the table that contains the row data
 * @return archive tag of row
 */
export function getArchiveTag(rowKey: string, tableView: TableView): RetentionTagType {
    return getRowDataProperty(
        rowKey,
        tableView,
        MailRowDataPropertyType.ArchiveTag
    ) as RetentionTagType;
}

/**
 * Get policy tag for a row
 * @param rowKey rowKey of row
 * @param tableView the table that contains the row data
 * @return policy tag of row
 */
export function getPolicyTag(rowKey: string, tableView: TableView): RetentionTagType {
    return getRowDataProperty(
        rowKey,
        tableView,
        MailRowDataPropertyType.PolicyTag
    ) as RetentionTagType;
}

/**
 * Get the specified property from the row
 * @param rowKey rowKey of row
 * @param tableView the table that contains the row data
 * @param propertyType to retrieve on the row data
 */
function getRowDataProperty(
    rowKey: string,
    tableView: TableView,
    propertyType: MailRowDataPropertyType
) {
    switch (tableView.tableQuery.listViewType) {
        case ReactListViewType.Conversation:
            return getConversationProperty(rowKey, tableView, propertyType);

        case ReactListViewType.Message:
            return getItemProperty(rowKey, tableView, propertyType);

        default:
            return null;
    }
}

/**
 * Get the specified property on the item
 * @param conversationRowKey the rowKey of the conversation
 *  @param tableView the table that contains the row data
 * @param propertyType the MailRowDataPropertyType
 * @return the specified property on the conversation
 */
function getConversationProperty(
    conversationRowKey: string,
    tableView: TableView,
    propertyType: MailRowDataPropertyType
) {
    const tableConversationRelation = getTableConversationRelation(
        conversationRowKey,
        tableView.id
    );

    if (!tableConversationRelation) {
        traceErrorString(
            'getConversationProperty should not be called on conversation that not in cache.',
            propertyType
        );

        return null;
    }

    const conversationId = tableConversationRelation.id;
    const conversationItem = selectConversationById(conversationId);
    const conversationItemParts = mailStore.conversations.get(conversationId);
    const firstItem = mailStore.items.get(tableConversationRelation.itemIds[0]);

    switch (propertyType) {
        case MailRowDataPropertyType.RowIdString:
            return tableConversationRelation.id;

        case MailRowDataPropertyType.RowClientItemId:
            return conversationItem.clientId;

        case MailRowDataPropertyType.ConversationId:
            return conversationId;

        case MailRowDataPropertyType.FlagStatus:
            return tableConversationRelation.flagStatus;

        case MailRowDataPropertyType.IsPinned:
            return pinningUtils.getIsTableConversationRelationPinned(tableConversationRelation);

        case MailRowDataPropertyType.ItemIds:
            return tableConversationRelation.itemIds;

        case MailRowDataPropertyType.LastDeliveryOrRenewTimeStamp:
            return tableConversationRelation.lastDeliveryOrRenewTimeStamp;

        case MailRowDataPropertyType.LastDeliveryTimeStamp:
            return tableConversationRelation.lastDeliveryTimeStamp;

        case MailRowDataPropertyType.LastModifiedTimeStamp:
            return tableConversationRelation.lastModifiedTimeStamp;

        case MailRowDataPropertyType.UnreadCount:
            return tableConversationRelation.unreadCount;

        case MailRowDataPropertyType.LastSenderMailbox:
            return (
                tableConversationRelation.lastSender && tableConversationRelation.lastSender.Mailbox // lastSender is undefined in rowModified notification payload, if the message is directly POSTed
            );

        case MailRowDataPropertyType.LastSenderSMTP:
            return (
                tableConversationRelation.lastSender && // lastSender is undefined in rowModified notification payload, if the message is directly POSTed
                tableConversationRelation.lastSender.Mailbox.EmailAddress
            );

        case MailRowDataPropertyType.ExecuteSearchSortOrder:
            return tableConversationRelation.executeSearchSortOrder;

        case MailRowDataPropertyType.ParentFolderId:
            if (tableView.tableQuery.type === TableQueryType.Search) {
                return tableConversationRelation.parentFolderId;
            } else {
                // Use tableView.tableQuery.folderId for non-search scenarios, because parentFolderId is not returned for findConversation
                return tableView.tableQuery.folderId;
            }

        case MailRowDataPropertyType.Subject:
            return (conversationItem as BaseItem).subject;

        case MailRowDataPropertyType.UniqueSenders:
            return tableConversationRelation.uniqueSenders;

        case MailRowDataPropertyType.Size:
            return conversationItem.size;

        case MailRowDataPropertyType.EffectiveMentioned:
            return tableConversationRelation.effectiveMentioned;

        case MailRowDataPropertyType.InstrumentationContext:
            return tableConversationRelation.instrumentationContext;

        case MailRowDataPropertyType.Categories:
            return tableConversationRelation.categories;

        case MailRowDataPropertyType.CanDelete:
            return conversationItemParts && conversationItemParts.canDelete;

        case MailRowDataPropertyType.ValidCouponIndexes:
            return conversationItem.validCouponIndexes;

        case MailRowDataPropertyType.Importance:
            return tableConversationRelation.importance;

        case MailRowDataPropertyType.ArchiveTag:
            return firstItem && firstItem.ArchiveTag;

        case MailRowDataPropertyType.PolicyTag:
            return firstItem && firstItem.PolicyTag;

        case MailRowDataPropertyType.IsSnoozed:
            return getIsTableConversationRelationSnoozed(tableConversationRelation);

        case MailRowDataPropertyType.IsSubmitted:
        case MailRowDataPropertyType.IsDraft:
        default:
            return null;
    }
}

/**
 * Get the specified property on the item
 * @param itemRowKey the rowKey for the item
 * @param tableView the table that contains the row data
 * @param propertyType the MailRowDataPropertyType
 * @return the specified property on the item
 */
function getItemProperty(
    itemRowKey: string,
    tableView: TableView,
    propertyType: MailRowDataPropertyType
) {
    const itemRelation = getTableItemRelation(itemRowKey, tableView.id);
    if (!itemRelation) {
        traceErrorString('getItemProperty: item not in item relations.', propertyType);
        return null;
    }

    const itemId = itemRelation.clientId;
    const item = mailStore.items.get(itemId.Id);

    if (!item) {
        traceErrorString('getItemProperty: Item not in item cache.', propertyType);
        return null;
    }

    const message = isItemOfMessageType(item) ? (item as Message) : null;
    const lastSenderMailBox = message && message.From && message.From.Mailbox;

    switch (propertyType) {
        case MailRowDataPropertyType.RowIdString:
            return itemId.Id;

        case MailRowDataPropertyType.RowClientItemId:
            //TODO: 22724 we need to use our own item type which has clientItemId instead of retrieving from tableItemRelation
            return itemId;

        case MailRowDataPropertyType.ConversationId:
            return message.ConversationId.Id;

        case MailRowDataPropertyType.FlagStatus:
            // item.Flag could be undefined in search results
            return item.Flag ? item.Flag.FlagStatus : null;

        case MailRowDataPropertyType.IsPinned:
            return pinningUtils.getIsTableItemDataPinned(item);

        case MailRowDataPropertyType.ItemIds:
            return [itemId.Id];

        case MailRowDataPropertyType.LastDeliveryOrRenewTimeStamp:
            return item.ReceivedOrRenewTime;

        case MailRowDataPropertyType.LastDeliveryTimeStamp:
            return item.DateTimeReceived;

        case MailRowDataPropertyType.LastModifiedTimeStamp:
            return item.LastModifiedTime;

        case MailRowDataPropertyType.UnreadCount:
            // returns 1 if message exists and it's unread
            // returns 0 if message is read, or item is not Message type
            return message && !message.IsRead ? 1 : 0;

        case MailRowDataPropertyType.LastSenderMailbox:
            return lastSenderMailBox;

        case MailRowDataPropertyType.LastSenderSMTP:
            return lastSenderMailBox && lastSenderMailBox.EmailAddress;

        case MailRowDataPropertyType.UniqueSenders:
            if (lastSenderMailBox) {
                return [lastSenderMailBox.Name];
            }
            return null;

        case MailRowDataPropertyType.ExecuteSearchSortOrder:
            // The SortOrderSource depends on each search table, so it should be accessed from item relation
            return itemRelation.executeSearchSortOrder;

        case MailRowDataPropertyType.ParentFolderId:
            return item.ParentFolderId.Id;

        case MailRowDataPropertyType.Subject:
            return item.Subject;

        case MailRowDataPropertyType.Size:
            return item.Size;
        case MailRowDataPropertyType.EffectiveMentioned:
            return item.MentionedMe;

        case MailRowDataPropertyType.IsDraft:
            return item.IsDraft;

        case MailRowDataPropertyType.IsSubmitted:
            return item.IsSubmitted;

        case MailRowDataPropertyType.InstrumentationContext:
            return itemRelation.instrumentationContext;

        case MailRowDataPropertyType.Categories:
            return item.Categories;

        case MailRowDataPropertyType.CanDelete:
            return item.CanDelete;

        case MailRowDataPropertyType.ValidCouponIndexes:
            return item.validCouponIndexes;

        case MailRowDataPropertyType.Importance:
            return item.Importance;

        case MailRowDataPropertyType.ArchiveTag:
            return item.ArchiveTag;

        case MailRowDataPropertyType.PolicyTag:
            return item.PolicyTag;

        case MailRowDataPropertyType.IsSnoozed:
            return getIsTableItemDataSnoozed(item);

        default:
            return null;
    }
}

function traceErrorString(error: string, propertyType: MailRowDataPropertyType) {
    const datapoint = returnTopExecutingActionDatapoint();
    let errorString = error + ' PropertyType:' + propertyType;
    if (datapoint) {
        errorString += ' Scenario:' + datapoint.eventName;
    }

    trace.error(errorString);
}
